(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Single shared "is this a real desktop pointer" check, reused by every
  // GSAP scroll-linked / hover-driven animation module below. GSAP's
  // ScrollTrigger and mousemove-based effects (parallax, magnetic buttons,
  // card tilt) rely on assumptions — a single continuous scroll position,
  // a hoverable cursor — that don't hold on touch devices: mobile browsers
  // resize the viewport as the address bar shows/hides (throwing off
  // ScrollTrigger's start/end math), and there's no real hover state to
  // drive tilt/magnetic effects. Restricting these to desktop (fine
  // pointer + hover capable) instead of trying to patch each symptom
  // individually is what actually fixes the "broken on mobile" behavior.
  var isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  // On slower connections (mobile in particular) the GSAP CDN script can
  // still be downloading/parsing when this script executes, so a single
  // synchronous `typeof window.gsap !== "undefined"` check here can come
  // back false even though GSAP loads a moment later. `window.__hasGSAP` used to be
  // a one-time boolean; it's now a live getter so every call site always
  // reflects GSAP's actual current availability instead of a stale snapshot.
  Object.defineProperty(window, "__hasGSAP", {
    get: function () { return typeof window.gsap !== "undefined"; },
    configurable: true
  });
  function registerGSAPPlugins() {
    if (!window.__hasGSAP) return;
    if (window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      // This is the actual, documented fix for the "ScrollTrigger glitches
      // on mobile" problem this file used to work around by disabling
      // ScrollTrigger-based animations on mobile entirely: address-bar
      // show/hide on phones fires a resize event, and by default
      // ScrollTrigger reacts by recalculating every trigger's start/end,
      // which is what caused the drift. Telling it to ignore resizes that
      // are caused by mobile viewport (address bar) changes keeps every
      // scroll-based animation below safe to run on phones too.
      ScrollTrigger.config({ ignoreMobileResize: true });
    }
    if (window.ScrollToPlugin) gsap.registerPlugin(ScrollToPlugin);
  }
  registerGSAPPlugins();

  /* =========================================================
     LUXURY SMOOTH FULL-PAGE SCROLL — pure vanilla JS/CSS
     Fixed-position content layer, lerped toward native scroll
     position on each animation frame. Deliberately has ZERO
     dependency on GSAP for its own operation: the paint step
     below always uses a plain CSS transform, never gsap.set().
     GSAP can fail to load, load late, or throw during plugin
     registration on a bad connection — none of that can break
     this module or leave the page stuck, because it never calls
     into GSAP to do its own job. If GSAP + ScrollTrigger *do*
     happen to be present, this optionally keeps their scroll-
     linked effects (parallax, reveals) in sync with the visual
     lerped position — but that sync is a bonus, not a dependency,
     and is wrapped defensively so a GSAP-side problem can't stop
     the scroll itself from working.
     Falls back to normal document flow if reduced motion is
     requested, required APIs are missing, or on touch devices
     (native mobile scrolling is already smooth, and this fixed-
     layer technique is a known source of address-bar-resize and
     nested-scroller bugs on touch — see notes below).
     ========================================================= */
  (function smoothScrollModule() {
    var wrapper = document.getElementById("smooth-wrapper");
    var content = document.getElementById("main");
    // This technique — a position:fixed content layer manually translated
    // to mimic scrolling, while the real page scroll happens invisibly
    // behind it — is only safe on devices with a real mouse/trackpad.
    // On touch devices it's a well-known source of body.style.height
    // (set below from content.scrollHeight) drifting out of sync with
    // the actual rendered height as mobile browsers resize their
    // viewport when the address bar shows/hides (extra scrollable
    // whitespace at the bottom), and the hidden "real" scroll position
    // fighting nested scrollers — the carousel's own horizontal touch-
    // scroll, and the back-to-top target — for which gesture owns the
    // touch. Falling back to native scroll on touch sidesteps all of
    // that; native mobile scrolling is already smooth.
    if (!wrapper || !content || reduceMotion || !isDesktop) return;

    document.body.classList.add("has-smooth-scroll");

    var current = window.pageYOffset || 0;
    var target = current;
    // Time-based (not frame-based) easing: TAU is the "time constant" in
    // seconds — roughly how long the lerp takes to close ~63% of the gap.
    // Using elapsed real time (not a flat per-frame multiplier) keeps the
    // exact same drift speed and feel whether the display refreshes at
    // 60Hz, 120Hz, or 144Hz, instead of the effect running faster on
    // high-refresh-rate screens.
    var TAU = 0.19;
    var raf = null;
    var lastTime = null;

    function setBodyHeight() {
      document.body.style.height = content.scrollHeight + "px";
    }
    setBodyHeight();

    // Optional, defensive sync so other GSAP/ScrollTrigger-driven effects
    // (parallax, staggered reveals) still fire in step with the visual
    // (lerped) position instead of the hidden real scroll position. Wrapped
    // in try/catch so any GSAP-side issue here can never break the smooth
    // scroll loop itself, which doesn't need this to function.
    var scrollTriggerSynced = false;
    function trySetupScrollTriggerSync() {
      if (scrollTriggerSynced) return;
      if (!window.__hasGSAP || !window.ScrollTrigger) return;
      if (typeof ScrollTrigger.scrollerProxy !== "function") return;
      try {
        ScrollTrigger.scrollerProxy(wrapper, {
          scrollTop: function (value) {
            if (arguments.length) { target = value; }
            return target;
          },
          getBoundingClientRect: function () {
            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
          },
          pinType: "transform"
        });
        ScrollTrigger.defaults({ scroller: wrapper });
        scrollTriggerSynced = true;
      } catch (e) {
        // GSAP/ScrollTrigger present but not fully ready or otherwise
        // failed — ignore and keep scrolling natively-driven; other
        // effects just won't be perfectly in lockstep this run.
      }
    }
    trySetupScrollTriggerSync();

    function tick(now) {
      if (lastTime === null) lastTime = now;
      var dt = (now - lastTime) / 1000;
      // Clamp dt so returning to a backgrounded/throttled tab doesn't
      // produce one huge catch-up jump on the first frame back.
      if (dt > 0.1) dt = 0.1;
      lastTime = now;

      target = window.pageYOffset || 0;
      var factor = 1 - Math.exp(-dt / TAU);
      current += (target - current) * factor;
      if (Math.abs(target - current) < 0.05) current = target;

      /* Round to a whole pixel before painting. Firefox in particular will
         visibly shimmer/"dance" any child that has its own animation
         (like .hero-img's zoom) when its ancestor sits at a fractional
         subpixel transform offset every frame — rounding here removes
         that mismatch without changing the perceived smoothness. */
      var painted = Math.round(current);
      content.style.transform = "translate3d(0," + (-painted) + "px,0)";

      if (!scrollTriggerSynced) trySetupScrollTriggerSync();
      if (scrollTriggerSynced && window.ScrollTrigger) {
        try { ScrollTrigger.update(); } catch (e) { /* ignore */ }
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    var refreshTimer = null;
    function scheduleRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        setBodyHeight();
        if (scrollTriggerSynced && window.ScrollTrigger) {
          try { ScrollTrigger.refresh(); } catch (e) { /* ignore */ }
        }
      }, 120);
    }

    if ("ResizeObserver" in window) {
      new ResizeObserver(scheduleRefresh).observe(content);
    } else {
      window.addEventListener("resize", scheduleRefresh);
    }
    window.addEventListener("load", scheduleRefresh);
    // Images/fonts can change layout height after initial load completes
    setTimeout(scheduleRefresh, 5000);
    setTimeout(scheduleRefresh, 6500);
  })();

  /* =========================================================
     LOADING SCREEN
     ========================================================= */
  (function loader() {
    var loaderEl = document.getElementById("loader");
    if (!loaderEl) return;

    // Show the full loader only on the very first page load of a browser
    // session. This is a classic multi-page Shopify theme (every link click
    // is a real full-page navigation, not an SPA/PJAX transition), so
    // without this check the loader markup re-renders and re-plays on
    // every single page. sessionStorage persists across page loads within
    // the same tab but clears when the tab/browser closes, which matches
    // "once per visit" exactly. From here on, every other page just uses
    // Shopify's normal default page load with no custom overlay.
    var LOADER_SESSION_KEY = "tenutaAureaLoaderShown";
    var alreadyShownThisSession = false;
    try {
      alreadyShownThisSession = sessionStorage.getItem(LOADER_SESSION_KEY) === "1";
    } catch (e) {
      // Storage unavailable (private browsing, disabled storage, etc.) —
      // fail open and just show the loader as before rather than erroring.
      alreadyShownThisSession = false;
    }
    if (alreadyShownThisSession) {
      loaderEl.style.display = "none";
      return;
    }
    try { sessionStorage.setItem(LOADER_SESSION_KEY, "1"); } catch (e) { /* ignore */ }

    document.body.classList.add("is-loading");

    var fillRect = loaderEl.querySelector(".loader-fill");
    var pctEl = document.getElementById("loader-pct");
    var GLASS_TOP = 90, GLASS_BOTTOM = 20, GLASS_HEIGHT = GLASS_TOP - GLASS_BOTTOM;
    var progress = { v: 0 };
    var finished = false;

    function paint() {
      if (fillRect) {
        var h = GLASS_HEIGHT * (progress.v / 100);
        fillRect.setAttribute("height", h.toFixed(1));
        fillRect.setAttribute("y", (GLASS_TOP - h).toFixed(1));
      }
      if (pctEl) pctEl.textContent = Math.round(progress.v) + "%";
    }

    function hide() {
      loaderEl.classList.add("is-hidden");
      document.body.classList.remove("is-loading");
      setTimeout(function () {
        loaderEl.style.display = "none";
        if (window.__hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
      }, 650);
    }

    // Manual rAF tween used whenever GSAP isn't available, so the fallback
    // path animates instead of snapping instantly. Simple ease-out curve
    // to roughly match GSAP's "power1.out"/"power1.inOut" feel.
    function rafTween(from, to, duration, easeOut, onUpdate, onComplete) {
      var start = null;
      function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
      function easeOutFn(t) { return 1 - Math.pow(1 - t, 2); }
      var ease = easeOut ? easeOutFn : easeInOut;
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / duration);
        progress.v = from + (to - from) * ease(t);
        onUpdate();
        if (t < 1) {
          requestAnimationFrame(step);
        } else if (onComplete) {
          onComplete();
        }
      }
      requestAnimationFrame(step);
    }

    function runInitialFill() {
      if (window.__hasGSAP) {
        gsap.to(progress, { v: 82, duration: 1.6, ease: "power1.inOut", onUpdate: paint });
      } else {
        rafTween(0, 82, 1600, false, paint);
      }
    }

    function finish() {
      if (finished) return;
      finished = true;
      if (window.__hasGSAP) {
        gsap.to(progress, { v: 100, duration: .5, ease: "power1.out", onUpdate: paint, onComplete: hide });
      } else {
        rafTween(progress.v, 100, 500, true, paint, hide);
      }
    }

    // GSAP's CDN <script> can still be in flight on slower (mobile)
    // connections when this file executes, so a single synchronous check
    // used to permanently lock the loader into the un-animated fallback
    // for the whole session even once GSAP finished loading a moment
    // later. Give it a short window to arrive before committing to a path.
    var GSAP_WAIT_MS = 400;
    var waited = 0;
    var GSAP_POLL_MS = 25;
    (function waitForGSAPThenStart() {
      if (window.__hasGSAP || waited >= GSAP_WAIT_MS) {
        registerGSAPPlugins();
        runInitialFill();
        return;
      }
      waited += GSAP_POLL_MS;
      setTimeout(waitForGSAPThenStart, GSAP_POLL_MS);
    })();

    if (document.readyState === "complete") {
      finish();
    } else {
      window.addEventListener("load", finish);
      setTimeout(finish, 3200); // safety fallback so users are never stuck
    }
  })();

  /* =========================================================
     STICKY HEADER + BACK TO TOP
     ========================================================= */
  var header = document.getElementById("site-header");
  var backToTop = document.querySelector(".back-to-top");

  // Hide the fixed header on scroll-down, reveal it again on scroll-up.
  // HIDE_THRESHOLD keeps it visible near the top (so it doesn't vanish
  // the instant the page moves), and DIRECTION_DELTA ignores tiny jitter
  // from momentum/bounce scrolling on mobile so it doesn't flicker.
  var lastY = window.scrollY || window.pageYOffset;
  var HIDE_THRESHOLD = 80;
  var DIRECTION_DELTA = 5;

  function onScroll() {
    var y = window.scrollY || window.pageYOffset;
    if (header) {
      header.classList.toggle("scrolled", y > 60);

      var delta = y - lastY;
      if (y <= HIDE_THRESHOLD) {
        header.classList.remove("header-hidden");
      } else if (delta > DIRECTION_DELTA) {
        header.classList.add("header-hidden");
      } else if (delta < -DIRECTION_DELTA) {
        header.classList.remove("header-hidden");
      }
    }
    if (backToTop) backToTop.classList.toggle("visible", y > 700);
    lastY = y;
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* =========================================================
     SMOOTH, GSAP-POWERED ANCHOR / IN-PAGE SCROLLING
     ========================================================= */
  function gsapScrollAvailable() {
    // Was a one-time snapshot taken at parse time — on a slower (mobile)
    // connection the GSAP CDN script can still be loading at that exact
    // moment, permanently locking this into "false" and the plain
    // window.scrollTo fallback for the rest of the session even once GSAP
    // finished loading a moment later. Checking live avoids that.
    //
    // Restricted to desktop on purpose: GSAP's ScrollToPlugin runs with
    // autoKill: true below, which cancels the tween the moment it detects
    // what it thinks is user-initiated scrolling (wheel/touch input). On
    // touch devices the tap that triggers this scroll itself generates a
    // touchstart/touchmove, which autoKill reads as the user scrolling
    // manually and kills the animation before the page moves at all —
    // this is exactly why "back to top" (and in-page anchor links) could
    // silently do nothing on mobile while working fine on desktop. Native
    // smooth scrolling below has no such conflict, so touch devices use
    // that instead.
    return isDesktop && window.__hasGSAP && typeof window.ScrollToPlugin !== "undefined";
  }

  function smoothScrollTo(el) {
    if (gsapScrollAvailable()) {
      gsap.to(window, { duration: 1.1, scrollTo: { y: el || 0, autoKill: true }, ease: "power3.inOut" });
      return;
    }
    // Mobile: jump instantly rather than using the browser's native
    // "smooth" scroll-behavior. Requested explicitly, and it also sidesteps
    // a real mobile pain point — native smooth scroll runs on the compositor
    // outside JS's control, so on longer pages / lower-powered phones it can
    // visibly stutter or fight momentum scrolling in a way an instant jump
    // never can. Desktop without GSAP (e.g. it failed to load) still gets a
    // smooth native scroll rather than a hard jump.
    var instant = !isDesktop || reduceMotion;
    if (el) {
      el.scrollIntoView({ behavior: instant ? "auto" : "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: instant ? "auto" : "smooth" });
    }
  }

  document.querySelectorAll('a[href^="#"]').forEach(function (link) {
    link.addEventListener("click", function (e) {
      var hash = link.getAttribute("href");
      if (!hash || hash === "#") return;
      var targetEl = document.querySelector(hash);
      if (!targetEl) return;
      e.preventDefault();
      closeNavOverlay();
      smoothScrollTo(targetEl);
      history.pushState(null, "", hash);
    });
  });

  if (backToTop) {
    backToTop.addEventListener("click", function (e) {
      e.preventDefault();
      try {
        smoothScrollTo(null);
      } catch (err) {
        // Absolute last resort — guarantees the button always does
        // *something* even if GSAP is in a broken/half-loaded state.
        window.scrollTo(0, 0);
      }
    });
  }

  /* =========================================================
     FULLSCREEN NAV OVERLAY
     ========================================================= */
  var menuToggle = document.querySelector(".menu-toggle");
  var navOverlay = document.getElementById("nav-overlay");
  var navClose = navOverlay ? navOverlay.querySelector(".nav-overlay-close") : null;
  var navLinks = navOverlay ? navOverlay.querySelectorAll(".nav-overlay-links a") : [];

  function openNavOverlay() {
    if (!navOverlay) return;
    navOverlay.classList.add("is-open");
    navOverlay.setAttribute("aria-hidden", "false");
    document.body.classList.add("nav-open");
    if (menuToggle) menuToggle.setAttribute("aria-expanded", "true");
    if (window.__hasGSAP) {
      gsap.fromTo(navLinks,
        { autoAlpha: 0, y: 26 },
        { autoAlpha: 1, y: 0, duration: .6, stagger: .06, ease: "power3.out", delay: .25 }
      );
    }
  }
  function closeNavOverlay() {
    if (!navOverlay || !navOverlay.classList.contains("is-open")) return;
    navOverlay.classList.remove("is-open");
    navOverlay.setAttribute("aria-hidden", "true");
    document.body.classList.remove("nav-open");
    if (menuToggle) menuToggle.setAttribute("aria-expanded", "false");
  }
  if (menuToggle && navOverlay) {
    menuToggle.addEventListener("click", function () {
      if (navOverlay.classList.contains("is-open")) closeNavOverlay();
      else openNavOverlay();
    });
  }
  if (navClose) navClose.addEventListener("click", closeNavOverlay);
  navLinks.forEach(function (a) { a.addEventListener("click", closeNavOverlay); });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closeNavOverlay();
  });

  /* Legacy inline mobile nav kept as a no-JS fallback; hide it if the overlay exists */
  var mobileNav = document.getElementById("mobile-nav");
  if (mobileNav && navOverlay) mobileNav.style.display = "none";

  /* =========================================================
     HEADER ACCOUNT MENU (logged-in dropdown)
     Toggled button/dropdown pair for signed-in customers —
     profile, order status, log out. Only present in the DOM when
     {% if customer %} in header.liquid, so this module simply
     no-ops on logged-out pages.
     ========================================================= */
  (function accountMenuModule() {
    var toggle = document.getElementById("account-menu-toggle");
    var menu = document.getElementById("account-menu");
    if (!toggle || !menu) return;

    function openMenu() {
      menu.classList.add("is-open");
      menu.setAttribute("aria-hidden", "false");
      toggle.setAttribute("aria-expanded", "true");
    }
    function closeMenu() {
      if (!menu.classList.contains("is-open")) return;
      menu.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      toggle.setAttribute("aria-expanded", "false");
    }

    toggle.addEventListener("click", function (e) {
      e.stopPropagation();
      if (menu.classList.contains("is-open")) closeMenu();
      else openMenu();
    });
    document.addEventListener("click", function (e) {
      if (!menu.classList.contains("is-open")) return;
      if (menu.contains(e.target) || toggle.contains(e.target)) return;
      closeMenu();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeMenu();
    });
  })();

  /* =========================================================
     NAV OVERLAY — image swap on link hover/focus
     ========================================================= */
  (function navOverlayImageSwapModule() {
    var linksNav = navOverlay ? navOverlay.querySelector(".nav-overlay-links") : null;
    var imgA = document.getElementById("nav-overlay-img-a");
    var imgB = document.getElementById("nav-overlay-img-b");
    var caption = document.getElementById("nav-overlay-caption");
    var eyebrowEl = caption ? caption.querySelector("span") : null;
    var captionEl = caption ? caption.querySelector("p") : null;
    if (!linksNav || !imgA || !imgB || !navLinks.length) return;

    var defaultImage = imgA.getAttribute("src");
    var defaultEyebrow = eyebrowEl ? eyebrowEl.textContent : "";
    var defaultCaption = captionEl ? captionEl.textContent : "";
    var activeIsA = true;
    var revertTimer = null;
    var captionTimer = null;

    function setImage(src) {
      if (!src) return;
      var showing = activeIsA ? imgA : imgB;
      var hidden = activeIsA ? imgB : imgA;
      if (showing.getAttribute("src") === src) return;
      hidden.setAttribute("src", src);
      hidden.classList.add("is-active");
      showing.classList.remove("is-active");
      activeIsA = !activeIsA;
    }

    function setCaption(eyebrowText, captionText) {
      if (!eyebrowEl || !captionEl) return;
      clearTimeout(captionTimer);
      eyebrowEl.style.opacity = "0";
      captionEl.style.opacity = "0";
      captionTimer = setTimeout(function () {
        eyebrowEl.textContent = eyebrowText;
        captionEl.textContent = captionText;
        eyebrowEl.style.opacity = "1";
        captionEl.style.opacity = "1";
      }, 220);
    }

    navLinks.forEach(function (link) {
      var img = link.getAttribute("data-nav-image");
      var eyebrowVal = link.getAttribute("data-nav-eyebrow") || defaultEyebrow;
      var captionVal = link.getAttribute("data-nav-caption") || defaultCaption;
      function activate() {
        clearTimeout(revertTimer);
        setImage(img);
        setCaption(eyebrowVal, captionVal);
      }
      link.addEventListener("mouseenter", activate);
      link.addEventListener("focus", activate);
    });

    linksNav.addEventListener("mouseleave", function () {
      revertTimer = setTimeout(function () {
        setImage(defaultImage);
        setCaption(defaultEyebrow, defaultCaption);
      }, 250);
    });
    linksNav.addEventListener("focusout", function (e) {
      if (linksNav.contains(e.relatedTarget)) return;
      revertTimer = setTimeout(function () {
        setImage(defaultImage);
        setCaption(defaultEyebrow, defaultCaption);
      }, 250);
    });
  })();

  /* =========================================================
     SCROLL REVEAL (IntersectionObserver)
     ========================================================= */
  var revealEls = document.querySelectorAll(".reveal");
  // Runs on every device. Unlike the GSAP/ScrollTrigger-driven effects
  // elsewhere in this file, this one is plain IntersectionObserver with no
  // dependency on continuous scroll-position math, so it isn't affected by
  // the address-bar resize issues that motivated restricting those to
  // desktop — there's no technical reason to deny mobile this effect.
  if ("IntersectionObserver" in window && revealEls.length) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry, i) {
          if (entry.isIntersecting) {
            var el = entry.target;
            var delay = (i % 4) * 80;
            setTimeout(function () { el.classList.add("is-visible"); }, delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    revealEls.forEach(function (el) { io.observe(el); });

    // On a page/screen short enough that there's nothing to scroll, any
    // .reveal element sitting in the bottom 40px band cut off by the
    // rootMargin above can never register as "intersecting" — there's no
    // further scroll gesture to push it past that margin — so is-visible
    // never gets added and the text stays permanently blurred. Once,
    // shortly after load, check for that no-scrollbar case and reveal
    // anything still hidden.
    window.setTimeout(function () {
      if (document.documentElement.scrollHeight <= window.innerHeight + 1) {
        revealEls.forEach(function (el) { el.classList.add("is-visible"); });
      }
    }, 400);
  } else {
    revealEls.forEach(function (el) { el.classList.add("is-visible"); });
  }

  /* =========================================================
     ANIMATED TEXT (split into lines/words + scroll reveal)
     ========================================================= */
  function splitText(el) {
    var html = el.innerHTML;
    var lineChunks = html.split(/<br\s*\/?>/i);
    el.innerHTML = "";
    lineChunks.forEach(function (chunk) {
      var lineWrap = document.createElement("span");
      lineWrap.className = "split-line";
      chunk.trim().split(/\s+/).forEach(function (word, idx, arr) {
        var w = document.createElement("span");
        w.textContent = word + (idx < arr.length - 1 ? "\u00A0" : "");
        lineWrap.appendChild(w);
      });
      el.appendChild(lineWrap);
    });
    return el.querySelectorAll(".split-line > span");
  }

  document.querySelectorAll("[data-split-text]").forEach(function (el) {
    var words = splitText(el);
    if (!window.__hasGSAP || reduceMotion) return;
    gsap.set(words, { yPercent: 110, opacity: 0 });
    if (el.closest(".hero")) {
      /* Hero heading animates in immediately on load, not on scroll — safe on
         every device since it isn't tied to scroll position. */
      gsap.to(words, { yPercent: 0, opacity: 1, duration: 1, stagger: .035, ease: "power4.out", delay: 1.1 });
    } else if (isDesktop && window.ScrollTrigger) {
      /* Desktop-only. Section headings routinely sit inside a .reveal
         container, and that CSS class + IntersectionObserver system (see
         SCROLL REVEAL above) already animates transform/opacity on the same
         element on its own scroll threshold. Running this GSAP scroll-tied
         tween on mobile too meant two systems raced to move the same
         heading at two different trigger points — GSAP's inline styles
         winning over the CSS class rule — which is what showed up as a
         jump/flash right as a section scrolled into view. Desktop-only
         removes that collision; .reveal alone drives mobile. */
      gsap.to(words, {
        yPercent: 0, opacity: 1, duration: .9, stagger: .03, ease: "power4.out",
        scrollTrigger: { trigger: el, start: "top 88%" }
      });
    } else {
      /* Mobile, or desktop with no ScrollTrigger available: bring the words
         in right away instead of tying them to scroll. gsap.set() above
         already set opacity:0 on every device, so skipping both branches
         here would leave headings permanently invisible on mobile — this
         is what actually reveals them, with .reveal (on the heading's
         section) handling the move-up motion instead. */
      gsap.to(words, { yPercent: 0, opacity: 1, duration: .9, stagger: .03, ease: "power4.out" });
    }
  });

  /* =========================================================
     HERO ZOOM — GSAP-driven instead of CSS @keyframes
     Running this on GSAP's own ticker keeps it frame-synced with
     the page's smooth-scroll transform above (that loop is plain
     JS, not GSAP, but both run every animation frame in step).
     Mixing a native CSS animation on a child with a JS-driven
     transform on its ancestor is what caused the hero image to
     visibly shimmer/"dance" while scrolling in Firefox.
     ========================================================= */
  (function heroZoom() {
    // .hero-img is the homepage/product hero; .shop-banner-img is the shop
    // page's equivalent banner image. They were previously two different
    // selectors and this only ever looked for the first, so the shop page
    // silently never got the zoom treatment at all.
    var heroImgs = document.querySelectorAll(".hero-img, .shop-banner-img");
    if (!heroImgs.length) return;
    heroImgs.forEach(function (heroImg) {
      if (window.__hasGSAP && !reduceMotion) {
        // Runs on every device now. The Firefox "shimmer" this was originally
        // restricted to desktop to avoid only happens when this tween is
        // paired with the custom smooth-scroll transform on #main — and that
        // transform is itself still desktop-only (isDesktop-gated further up),
        // so on mobile there's no competing transform for it to clash with.
        gsap.fromTo(heroImg,
          { scale: 1 },
          { scale: 1.08, duration: 11, ease: "sine.inOut", yoyo: true, repeat: -1 }
        );
      } else if (!reduceMotion) {
        // Mobile (or GSAP failed to load) — use the plain CSS @keyframes
        // zoom instead, which doesn't depend on ScrollTrigger/rAF syncing.
        heroImg.classList.add("css-zoom-fallback");
      }
      // If reduced motion is requested, neither runs — the image stays still.
    });
  })();

  /* =========================================================
     GSAP SCROLLTRIGGER — AMBIENT PARALLAX / EMPHASIS
     ========================================================= */
  if (isDesktop && window.__hasGSAP && window.ScrollTrigger && !reduceMotion) {
    // Desktop-only. ScrollTrigger.config({ ignoreMobileResize: true }) above
    // does stop these start/end offsets drifting on a phone's address-bar
    // resize, but that's not the only mobile failure mode: .stats-list li
    // below animates the same opacity/transform that the .reveal class +
    // IntersectionObserver system (see SCROLL REVEAL above) already applies
    // to the same elements. Two systems writing to transform on the same
    // element, triggered at different scroll positions with different
    // durations/offsets, is what shows up as a flash/jump to the wrong
    // position — GSAP's inline styles always win over the CSS class rule,
    // so each system briefly "wins" and the element snaps between them.
    // Keeping this desktop-only leaves .reveal as the single source of
    // truth for the move-up-on-scroll effect on mobile.
    gsap.utils.toArray(".wine-feature img, .art-photo img").forEach(function (img) {
      gsap.fromTo(img, { scale: 1.12, y: -20 }, {
        scale: 1, y: 0, ease: "none",
        scrollTrigger: { trigger: img, start: "top bottom", end: "bottom top", scrub: .6 }
      });
    });
    gsap.utils.toArray(".stats-list li").forEach(function (li, i) {
      gsap.from(li, {
        opacity: 0, y: 24, duration: .7, delay: i * .08, ease: "power3.out",
        scrollTrigger: { trigger: ".stats-band", start: "top 85%" }
      });
    });
  }

  /* =========================================================
     PREMIUM HOVER EFFECTS — magnetic buttons + card tilt
     ========================================================= */
  // Both of these hover effects (magnetic buttons, 3D card tilt) are
  // pointer-position-driven and assume a real hoverable cursor. On touch
  // devices there's no genuine "hover" — mousemove/mouseenter fire at
  // unpredictable moments (usually right on tap) and mouseleave may never
  // fire at all, which is what left buttons/cards visibly stuck mid-
  // animation on mobile. Desktop-only avoids that entirely.
  if (isDesktop) {
    document.querySelectorAll(".btn").forEach(function (el) {
      el.addEventListener("mousemove", function (e) {
        var r = el.getBoundingClientRect();
        var mx = ((e.clientX - r.left) / r.width) * 100;
        var my = ((e.clientY - r.top) / r.height) * 100;
        el.style.setProperty("--mx", mx + "%");
        el.style.setProperty("--my", my + "%");
        if (window.__hasGSAP && !reduceMotion) {
          var relX = (e.clientX - r.left - r.width / 2) * .18;
          var relY = (e.clientY - r.top - r.height / 2) * .3;
          gsap.to(el, { x: relX, y: relY, duration: .35, ease: "power3.out" });
        }
      });
      el.addEventListener("mouseleave", function () {
        if (window.__hasGSAP && !reduceMotion) gsap.to(el, { x: 0, y: 0, duration: .5, ease: "elastic.out(1, .4)" });
      });
    });

    document.querySelectorAll(".wine-card img, .journal-img img, .art-photo img").forEach(function (img) {
      var card = img.closest(".wine-card, .journal-card, .art-photo") || img;
      card.addEventListener("mouseenter", function () {
        if (window.__hasGSAP) gsap.to(img, { scale: 1.04, duration: .4, ease: "power2.out" });
      });
      card.addEventListener("mousemove", function (e) {
        if (reduceMotion || !window.__hasGSAP) return;
        var r = card.getBoundingClientRect();
        var rx = ((e.clientY - r.top) / r.height - .5) * -6;
        var ry = ((e.clientX - r.left) / r.width - .5) * 6;
        gsap.to(img, { rotateX: rx, rotateY: ry, scale: 1.04, duration: .4, ease: "power2.out", transformPerspective: 600 });
      });
      card.addEventListener("mouseleave", function () {
        if (window.__hasGSAP) gsap.to(img, { rotateX: 0, rotateY: 0, scale: 1, duration: .6, ease: "power3.out" });
      });
    });
  }

  /* =========================================================
     PREMIUM TOUCH EFFECTS — mobile equivalent of the hover module
     above. mousemove/mouseenter never fire meaningfully on touch,
     so instead of trying to force the desktop hover math onto touch
     events, this gives touch users their own tactile feedback: a
     quick, springy press-in on tap for buttons, and a brief pop/
     highlight on card images, so mobile still feels alive instead
     of static. Guarded on window.__hasGSAP so it degrades to nothing
     (no error) if GSAP hasn't loaded.
     ========================================================= */
  if (!isDesktop && !reduceMotion) {
    document.querySelectorAll(".btn").forEach(function (el) {
      el.addEventListener("touchstart", function () {
        if (window.__hasGSAP) gsap.to(el, { scale: .95, duration: .18, ease: "power2.out" });
      }, { passive: true });
      el.addEventListener("touchend", function () {
        if (window.__hasGSAP) gsap.to(el, { scale: 1, duration: .45, ease: "elastic.out(1, .5)" });
      }, { passive: true });
    });

    document.querySelectorAll(".wine-card img, .journal-img img, .art-photo img").forEach(function (img) {
      var card = img.closest(".wine-card, .journal-card, .art-photo") || img;
      card.addEventListener("touchstart", function () {
        if (window.__hasGSAP) gsap.to(img, { scale: 1.05, duration: .3, ease: "power2.out" });
      }, { passive: true });
      card.addEventListener("touchend", function () {
        if (window.__hasGSAP) gsap.to(img, { scale: 1, duration: .5, ease: "power3.out" });
      }, { passive: true });
    });
  }

  /* =========================================================
     ADD-TO-CART MICRO INTERACTION
     ========================================================= */
  document.querySelectorAll(".btn-cart[data-variant-id]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      var original = btn.innerHTML;
      var variantId = btn.getAttribute("data-variant-id");
      if (!variantId) return;
      btn.disabled = true;
      window.TenutaCart.add(variantId, 1)
        .then(function () {
          btn.innerHTML = "Added \u2713";
          if (window.__hasGSAP) gsap.fromTo(btn, { scale: .92 }, { scale: 1, duration: .4, ease: "back.out(3)" });
          setTimeout(function () {
            btn.innerHTML = original;
            btn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          btn.innerHTML = "Couldn\u2019t add \u2014 retry";
          setTimeout(function () {
            btn.innerHTML = original;
            btn.disabled = false;
          }, 1800);
        });
    });
  });

  /* =========================================================
     LIGHTBOX / IMAGE + VIDEO GALLERY
     ========================================================= */
  (function lightboxModule() {
    var lb = document.getElementById("lightbox");
    if (!lb) return;
    var imgEl = document.getElementById("lightbox-img");
    var videoEl = document.getElementById("lightbox-video");
    var iframeEl = document.getElementById("lightbox-iframe");
    var captionEl = document.getElementById("lightbox-caption");
    var closeBtn = lb.querySelector(".lightbox-close");
    var prevBtn = lb.querySelector(".lightbox-prev");
    var nextBtn = lb.querySelector(".lightbox-next");

    function getYouTubeId(url) {
      var m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      return m ? m[1] : null;
    }
    function getInstagramEmbedUrl(url) {
      var clean = url.split("?")[0].replace(/\/$/, "");
      return clean + "/embed";
    }
    function detectVideoType(url) {
      if (!url) return null;
      if (/youtube\.com|youtu\.be/.test(url)) return "youtube";
      if (/instagram\.com/.test(url)) return "instagram";
      return "file";
    }

    var groups = {};
    document.querySelectorAll("[data-lightbox]").forEach(function (trigger) {
      var group = trigger.getAttribute("data-gallery") || "default";
      if (!groups[group]) groups[group] = [];
      var src = trigger.getAttribute("data-image") ||
        (trigger.tagName === "IMG" ? trigger.getAttribute("src") : (trigger.querySelector("img") ? trigger.querySelector("img").getAttribute("src") : ""));
      var videoSrc = trigger.getAttribute("data-video") || "";
      groups[group].push({ src: src, video: videoSrc, videoType: detectVideoType(videoSrc), caption: trigger.getAttribute("data-caption") || "" });
      var indexInGroup = groups[group].length - 1;
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openLightbox(group, indexInGroup);
      });
    });

    var activeGroup = null, activeIndex = 0;
    function openLightbox(group, index) {
      activeGroup = group;
      activeIndex = index;
      renderSlide();
      lb.classList.add("is-open");
      lb.setAttribute("aria-hidden", "false");
      document.body.classList.add("nav-open");
    }

    function hideAllMedia() {
      if (imgEl) imgEl.style.display = "none";
      if (videoEl) { videoEl.pause(); videoEl.removeAttribute("src"); videoEl.load(); videoEl.style.display = "none"; }
      if (iframeEl) { iframeEl.src = ""; iframeEl.style.display = "none"; }
    }

    function renderSlide() {
      var item = groups[activeGroup][activeIndex];
      if (!item) return;
      hideAllMedia();

      if (item.video && item.videoType === "file") {
        if (videoEl) {
          videoEl.src = item.video;
          videoEl.loop = true;
          videoEl.title = item.caption;
          videoEl.setAttribute("aria-label", item.caption);
          videoEl.style.display = "block";
          videoEl.play().catch(function () { /* controls remain visible as fallback */ });
        }
      } else if (item.video && item.videoType === "youtube") {
        var ytId = getYouTubeId(item.video);
        if (iframeEl && ytId) {
          iframeEl.title = item.caption;
          iframeEl.src = "https://www.youtube.com/embed/" + ytId + "?autoplay=1&rel=0";
          iframeEl.style.display = "block";
        }
      } else if (item.video && item.videoType === "instagram") {
        if (iframeEl) {
          iframeEl.title = item.caption;
          iframeEl.src = getInstagramEmbedUrl(item.video);
          iframeEl.style.display = "block";
        }
      } else if (imgEl) {
        imgEl.style.display = "";
        imgEl.src = item.src;
        imgEl.alt = item.caption;
        imgEl.title = item.caption;
      }
      captionEl.textContent = item.caption;
    }

    function closeLightbox() {
      lb.classList.remove("is-open");
      lb.setAttribute("aria-hidden", "true");
      document.body.classList.remove("nav-open");
      hideAllMedia();
    }
    function step(dir) {
      var list = groups[activeGroup];
      if (!list) return;
      activeIndex = (activeIndex + dir + list.length) % list.length;
      renderSlide();
    }
    if (closeBtn) closeBtn.addEventListener("click", closeLightbox);
    if (prevBtn) prevBtn.addEventListener("click", function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener("click", function () { step(1); });
    lb.addEventListener("click", function (e) { if (e.target === lb) closeLightbox(); });
    document.addEventListener("keydown", function (e) {
      if (!lb.classList.contains("is-open")) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    });
  })();

  /* =========================================================
     VIDEO PLAY BUTTON MICRO INTERACTION
     ========================================================= */
  document.querySelectorAll(".motion-play").forEach(function (btn) {
    btn.addEventListener("click", function () {
      if (window.__hasGSAP) {
        gsap.fromTo(btn, { scale: .97 }, { scale: 1, duration: .25, ease: "power2.out" });
      } else {
        btn.style.transform = "scale(0.97)";
        setTimeout(function () { btn.style.transform = ""; }, 180);
      }
    });
  });

  /* =========================================================
     RESPONSIVE CAROUSEL — wine-cards / motion-cards / journal-grid
     ========================================================= */
  (function carouselModule() {
    var carousels = document.querySelectorAll("[data-carousel]");
    if (!carousels.length) return;

    // Mirrors the CSS tiers in styles.css exactly (3 slides desktop,
    // 2 tablet, 1 mobile) so the JS's notion of "how many slides are
    // visible at once" can never drift out of sync with what's actually
    // rendered. Previously dots were built one-per-slide regardless of
    // how many were visible at once, so with 3 slides showing, the last
    // couple of dots pointed at scroll positions the browser clamps
    // before reaching (you can't scroll a 3-up view past showing the
    // final 3 items) — that's what caused the dots/buttons mismatch.
    var TABLET_MAX = 1024;
    var MOBILE_MAX = 640;
    // Per-carousel override of the tablet-tier item count. Wines shows
    // 1 item on both tablet and mobile (rather than the usual 2-on-tablet
    // default the other carousels use).
    var TABLET_ITEMS_OVERRIDE = { wines: 1 };
    function getItemsPerView(name) {
      var w = window.innerWidth;
      if (w <= MOBILE_MAX) return 1;
      if (w <= TABLET_MAX) return TABLET_ITEMS_OVERRIDE[name] || 2;
      return 3;
    }

    carousels.forEach(function (root) {
      var name = root.getAttribute("data-carousel-name");
      var track = root.querySelector(".carousel-track");
      var slides = Array.prototype.slice.call(root.querySelectorAll(".carousel-slide"));
      var prevBtn = root.querySelector(".carousel-prev");
      var nextBtn = root.querySelector(".carousel-next");
      var dotsWrap = root.querySelector("[data-carousel-dots]");
      if (!track || !slides.length) return;

      var active = false;
      var index = 0;       // current "leading" slide index, 0..maxIndex
      var itemsPerView = 1;
      var maxIndex = 0;    // last reachable leading-slide index

      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = "";
        for (var i = 0; i <= maxIndex; i++) {
          (function (dotIndex) {
            var b = document.createElement("button");
            b.type = "button";
            b.setAttribute("aria-label", "Go to slide " + (dotIndex + 1));
            b.addEventListener("click", function () { goTo(dotIndex); });
            dotsWrap.appendChild(b);
          })(i);
        }
      }

      // Finds the slide whose LEFT edge is closest to the track's left
      // edge — i.e. the slide currently snapped at the start of the
      // view — clamped to maxIndex since nothing past that point is a
      // distinct, reachable scroll position.
      function nearestIndex() {
        var trackLeft = track.getBoundingClientRect().left;
        var closest = 0, closestDist = Infinity;
        slides.forEach(function (s, i) {
          var dist = Math.abs(s.getBoundingClientRect().left - trackLeft);
          if (dist < closestDist) { closestDist = dist; closest = i; }
        });
        return Math.min(closest, maxIndex);
      }

      function updateDots() {
        if (!dotsWrap) return;
        Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
          dot.classList.toggle("is-active", i === index);
        });
      }

      // Manually animates track.scrollLeft with requestAnimationFrame
      // instead of relying on native scrollTo({behavior:"smooth"}).
      // Native smooth-scroll on an *element* (as opposed to the whole
      // page) has long been unreliable on mobile Safari and many Android
      // WebViews — it silently snaps to the new position instantly
      // instead of animating, which is exactly the "carousel just
      // changes, doesn't slide" symptom reported on mobile. Doing the
      // animation by hand guarantees it actually animates everywhere.
      var slideAnim = null;
      function animateScrollTo(destination) {
        if (slideAnim) cancelAnimationFrame(slideAnim);
        var start = track.scrollLeft;
        var distance = destination - start;
        if (Math.abs(distance) < 1) { track.scrollLeft = destination; return; }
        var duration = reduceMotion ? 150 : 420;
        var startTime = null;
        // Neutralize anything in the stylesheet that could hijack a
        // programmatic scrollLeft change — CSS scroll-behavior: smooth
        // fights this rAF loop's per-frame writes, and scroll-snap-type:
        // mandatory can force the browser to snap mid-animation. Using
        // setProperty(..., "important") guarantees this wins even if the
        // stylesheet rule itself is marked !important, which a plain
        // el.style.property assignment cannot override.
        var prevScrollBehavior = track.style.getPropertyValue("scroll-behavior");
        var prevScrollBehaviorPriority = track.style.getPropertyPriority("scroll-behavior");
        var prevSnapType = track.style.getPropertyValue("scroll-snap-type");
        var prevSnapPriority = track.style.getPropertyPriority("scroll-snap-type");
        track.style.setProperty("scroll-behavior", "auto", "important");
        track.style.setProperty("scroll-snap-type", "none", "important");
        function restoreStyles() {
          if (prevScrollBehavior) { track.style.setProperty("scroll-behavior", prevScrollBehavior, prevScrollBehaviorPriority); }
          else { track.style.removeProperty("scroll-behavior"); }
          if (prevSnapType) { track.style.setProperty("scroll-snap-type", prevSnapType, prevSnapPriority); }
          else { track.style.removeProperty("scroll-snap-type"); }
        }
        function easeInOutQuad(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
        function step(ts) {
          if (startTime === null) startTime = ts;
          var t = Math.min(1, (ts - startTime) / duration);
          track.scrollLeft = Math.round(start + distance * easeInOutQuad(t));
          if (t < 1) {
            slideAnim = requestAnimationFrame(step);
          } else {
            slideAnim = null;
            restoreStyles();
          }
        }
        slideAnim = requestAnimationFrame(step);
      }

      // Wraps within the reachable range (0..maxIndex) so Next past the
      // last reachable position loops smoothly back to the first, and
      // Previous before the first loops back to the last — using
      // maxIndex rather than slides.length keeps this in sync with what
      // multiple-slides-per-view can actually scroll to.
      function goTo(i) {
        var count = maxIndex + 1;
        index = ((i % count) + count) % count;
        var target = slides[index];
        if (!target) return;
        animateScrollTo(target.offsetLeft - track.offsetLeft);
        updateDots();
      }

      var scrollTicking = false;
      function onTrackScroll() {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(function () {
          index = nearestIndex();
          updateDots();
          scrollTicking = false;
        });
      }

      function activate() {
        if (active) return;
        active = true;
        root.classList.add("is-active");
        track.addEventListener("scroll", onTrackScroll, { passive: true });
      }
      function deactivate() {
        if (!active) return;
        active = false;
        root.classList.remove("is-active");
        track.removeEventListener("scroll", onTrackScroll);
      }

      if (prevBtn) prevBtn.addEventListener("click", function () { goTo(index - 1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { goTo(index + 1); });

      var resizeRAF = null;
      // items-per-view changes across the 3 -> 2 -> 1 breakpoints on
      // resize, so maxIndex/dots are recalculated every time, not just
      // on first activation.
      function checkBreakpoint() {
        itemsPerView = getItemsPerView(name);
        maxIndex = Math.max(0, slides.length - itemsPerView);
        if (slides.length > itemsPerView) {
          activate();
        } else {
          deactivate();
        }
        buildDots();
        index = Math.min(index, maxIndex);
        updateDots();
      }
      checkBreakpoint();
      window.addEventListener("resize", function () {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(checkBreakpoint);
      });
    });
  })();

  /* =========================================================
     AMBIENT BACKGROUND GLITCH PARTICLES (burgundy)
     ========================================================= */
  (function particlesModule() {
    var canvases = document.querySelectorAll("[data-particles]");
    if (!canvases.length || reduceMotion) return;

    var burgundy = getComputedStyle(document.documentElement).getPropertyValue("--burgundy").trim() || "#77232F";
    var gold = getComputedStyle(document.documentElement).getPropertyValue("--gold").trim() || "#C9A961";

    function hexToRgb(hex) {
      hex = hex.replace("#", "");
      if (hex.length === 3) hex = hex.split("").map(function (c) { return c + c; }).join("");
      var num = parseInt(hex, 16);
      return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    }
    var cBurgundy = hexToRgb(burgundy);
    var cGold = hexToRgb(gold);

    canvases.forEach(function (canvas) {
      var ctx = canvas.getContext("2d");

      var particles = [];
      var running = false;
      var w, h, dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rafId = null;

      function resize() {
        var rect = canvas.parentElement.getBoundingClientRect();
        w = rect.width; h = rect.height;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + "px";
        canvas.style.height = h + "px";
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      function seed() {
        var count = Math.max(18, Math.min(46, Math.round((w * h) / 26000)));
        particles = [];
        for (var i = 0; i < count; i++) {
          particles.push({
            x: Math.random() * w,
            y: Math.random() * h,
            r: Math.random() * 1.8 + .6,
            vx: (Math.random() - .5) * .18,
            vy: (Math.random() - .5) * .18,
            c: Math.random() > .8 ? cGold : cBurgundy,
            a: Math.random() * .5 + .2,
            glitch: Math.random() > .93
          });
        }
      }

      function draw() {
        ctx.clearRect(0, 0, w, h);
        particles.forEach(function (p) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < -10) p.x = w + 10; if (p.x > w + 10) p.x = -10;
          if (p.y < -10) p.y = h + 10; if (p.y > h + 10) p.y = -10;

          var jitterX = 0, jitterY = 0, alpha = p.a;
          if (p.glitch && Math.random() > .96) {
            jitterX = (Math.random() - .5) * 14;
            jitterY = (Math.random() - .5) * 6;
            alpha = Math.min(1, p.a + .4);
          }
          ctx.beginPath();
          ctx.fillStyle = "rgba(" + p.c.r + "," + p.c.g + "," + p.c.b + "," + alpha + ")";
          ctx.arc(p.x + jitterX, p.y + jitterY, p.r, 0, Math.PI * 2);
          ctx.fill();
        });
        rafId = requestAnimationFrame(draw);
      }

      function start() {
        if (running) return;
        running = true;
        resize();
        seed();
        draw();
      }
      function stop() {
        running = false;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      }

      if ("IntersectionObserver" in window) {
        var obs = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start(); else stop();
          });
        }, { threshold: .05 });
        obs.observe(canvas);
      } else {
        start();
      }

      var resizeRAF = null;
      window.addEventListener("resize", function () {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(function () {
          if (running) { resize(); seed(); }
        });
      });
    });
  })();

  /* =========================================================
     LIVE "WEATHER & VINE" — real conditions at the estate
     (Gambassi Terme, Tuscany), via the free Open-Meteo API.
     Falls back silently to the static placeholder values if
     the request fails or the visitor is offline.
     ========================================================= */
  (function weatherModule() {
    var grid = document.querySelector(".weather-grid");
    if (!grid) return;

    var ESTATE_LAT = 43.6117, ESTATE_LON = 10.9998, ESTATE_TZ = "Europe/Rome";
    var humidityEl = grid.querySelector('[data-weather="humidity"] [data-weather-value]');
    var tempEl = grid.querySelector('[data-weather="temperature"] [data-weather-value]');
    var windEl = grid.querySelector('[data-weather="wind"] [data-weather-value]');
    var badge = document.getElementById("weather-live-badge");
    var sourceEl = document.getElementById("weather-source");
    var panel = grid.closest(".footer-weather");

    var url = "https://api.open-meteo.com/v1/forecast"
      + "?latitude=" + ESTATE_LAT
      + "&longitude=" + ESTATE_LON
      + "&current=temperature_2m,relative_humidity_2m,wind_speed_10m"
      + "&timezone=" + encodeURIComponent(ESTATE_TZ);

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error("weather request failed");
        return res.json();
      })
      .then(function (data) {
        var c = data && data.current;
        if (!c) return;
        if (humidityEl && typeof c.relative_humidity_2m === "number") {
          humidityEl.textContent = Math.round(c.relative_humidity_2m) + "%";
        }
        if (tempEl && typeof c.temperature_2m === "number") {
          tempEl.textContent = Math.round(c.temperature_2m) + "\u00B0C";
        }
        if (windEl && typeof c.wind_speed_10m === "number") {
          windEl.textContent = Math.round(c.wind_speed_10m) + " km/h";
        }
        if (panel) panel.classList.add("is-live");
        if (badge) badge.setAttribute("aria-hidden", "false");
        if (sourceEl) sourceEl.textContent = "Live conditions at the estate, Gambassi Terme";
      })
      .catch(function () {
        if (sourceEl) sourceEl.textContent = "Estate conditions, Gambassi Terme (offline estimate)";
      });

    // Keep the report fresh while the tab stays open.
    setInterval(function () {
      fetch(url).then(function (res) { return res.ok ? res.json() : null; }).then(function (data) {
        var c = data && data.current;
        if (!c) return;
        if (humidityEl && typeof c.relative_humidity_2m === "number") humidityEl.textContent = Math.round(c.relative_humidity_2m) + "%";
        if (tempEl && typeof c.temperature_2m === "number") tempEl.textContent = Math.round(c.temperature_2m) + "\u00B0C";
        if (windEl && typeof c.wind_speed_10m === "number") windEl.textContent = Math.round(c.wind_speed_10m) + " km/h";
      }).catch(function () {});
    }, 15 * 60 * 1000);
  })();

  /* =========================================================
     LIVE ESTATE CLOCK (Italy local time, Europe/Rome)
     ========================================================= */
  var timeEl = document.getElementById("local-time");
  function updateClock() {
    if (!timeEl) return;
    try {
      var formatter = new Intl.DateTimeFormat([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Rome"
      });
      timeEl.textContent = formatter.format(new Date());
    } catch (e) {
      /* keep static fallback */
    }
  }
  updateClock();
  setInterval(updateClock, 30000);

  /* =========================================================
     SHOP — filters, sort, wishlist, mobile filter drawer
     ========================================================= */
  (function shopModule() {
    var shopSection = document.querySelector(".shop");
    var filtersRoot = document.querySelector("[data-shop-filters]");
    var grid = document.querySelector("[data-shop-grid]");
    if (!shopSection || !filtersRoot || !grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll("[data-shop-card]"));
    var emptyMsg = document.querySelector("[data-shop-empty]");
    var sortSelect = document.querySelector("[data-shop-sort]");
    var clearBtn = document.querySelector("[data-filters-clear]");
    var toggleBtn = document.querySelector("[data-filters-toggle]");
    var filtersPanel = document.querySelector("[data-filters-panel]");
    var groupToggles = document.querySelectorAll("[data-filter-group] > [data-filter-toggle]");
    var inputs = Array.prototype.slice.call(filtersRoot.querySelectorAll("[data-filter-input]"));

    // Remember original DOM order so "Featured" sort can restore it.
    cards.forEach(function (card, i) { card.dataset.originalOrder = i; });

    /* ---- accordion groups (Categories / Wine Style / Grape / Vintage) ---- */
    groupToggles.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var group = btn.closest("[data-filter-group]");
        if (group) group.classList.toggle("is-open");
      });
    });

    /* ---- mobile filters drawer ---- */
    if (toggleBtn && filtersPanel) {
      toggleBtn.addEventListener("click", function () {
        var isOpen = filtersRoot.getAttribute("data-open") === "true";
        filtersRoot.setAttribute("data-open", isOpen ? "false" : "true");
        toggleBtn.setAttribute("aria-expanded", isOpen ? "false" : "true");
      });
    }

    /* ---- collect the active filter selections, grouped by facet type ---- */
    function getActiveFilters() {
      var active = {};
      inputs.forEach(function (input) {
        if (!input.checked) return;
        var type = input.dataset.filterType;
        var value = input.value;
        if (type === "category" && value === "all") return; // "All Wines" imposes no restriction
        if (!active[type]) active[type] = [];
        active[type].push(value);
      });
      return active;
    }

    function cardMatches(card, active) {
      return Object.keys(active).every(function (type) {
        var cardValues = (card.dataset[type === "category" ? "category" : type] || "").split(/\s+/);
        return active[type].some(function (v) { return cardValues.indexOf(v) !== -1; });
      });
    }

    function applyFilters() {
      var active = getActiveFilters();
      var visibleCount = 0;
      cards.forEach(function (card) {
        var show = cardMatches(card, active);
        card.classList.toggle("is-hidden", !show);
        if (show) visibleCount++;
      });
      if (emptyMsg) emptyMsg.hidden = visibleCount !== 0;
    }

    /* Checking "All Wines" clears every other category checkbox; checking
       any specific category unchecks "All Wines". This keeps the facet
       group behaving the way shoppers expect from the design. */
    var allWinesInput = filtersRoot.querySelector('[data-filter-type="category"][value="all"]');
    inputs.forEach(function (input) {
      input.addEventListener("change", function () {
        if (input.dataset.filterType === "category") {
          if (input.value === "all" && input.checked) {
            inputs.forEach(function (other) {
              if (other !== input && other.dataset.filterType === "category") other.checked = false;
            });
          } else if (input.checked) {
            if (allWinesInput) allWinesInput.checked = false;
          } else {
            var anyCategoryChecked = inputs.some(function (other) {
              return other.dataset.filterType === "category" && other.value !== "all" && other.checked;
            });
            if (!anyCategoryChecked && allWinesInput) allWinesInput.checked = true;
          }
        }
        applyFilters();
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener("click", function () {
        inputs.forEach(function (input) {
          input.checked = input.dataset.filterType === "category" && input.value === "all";
        });
        applyFilters();
      });
    }

    /* ---- sorting ---- */
    function applySort() {
      if (!sortSelect) return;
      var mode = sortSelect.value;
      var sorted = cards.slice();
      if (mode === "price-asc") {
        sorted.sort(function (a, b) { return parseFloat(a.dataset.price) - parseFloat(b.dataset.price); });
      } else if (mode === "price-desc") {
        sorted.sort(function (a, b) { return parseFloat(b.dataset.price) - parseFloat(a.dataset.price); });
      } else if (mode === "name-asc") {
        sorted.sort(function (a, b) { return a.dataset.name.localeCompare(b.dataset.name); });
      } else {
        sorted.sort(function (a, b) { return a.dataset.originalOrder - b.dataset.originalOrder; });
      }
      sorted.forEach(function (card) { grid.appendChild(card); });
    }
    if (sortSelect) sortSelect.addEventListener("change", applySort);

    /* ---- wishlist toggle (visual only, no backend) ---- */
    grid.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-wishlist-btn]");
      if (!btn) return;
      btn.classList.toggle("is-active");
      var active = btn.classList.contains("is-active");
      var label = btn.getAttribute("aria-label") || "";
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      btn.setAttribute("aria-label", active ? label.replace("Add ", "Remove ") : label.replace("Remove ", "Add "));
    });

    applyFilters();
  })();

  /* =========================================================
     JOURNAL TAG FILTER — client-side, no page reload. All
     articles for the section are already in the DOM (see
     journal-list.liquid); clicking a tab shows/hides cards by
     their data-tags attribute with a staggered fade + rise
     animation instead of navigating to a /tagged/ URL.
     ========================================================= */
  (function journalFilterModule() {
    var tabsRoot = document.querySelector("[data-journal-tabs]");
    var grid = document.querySelector("[data-journal-grid]");
    if (!tabsRoot || !grid) return;

    var tabs = Array.prototype.slice.call(tabsRoot.querySelectorAll("[data-journal-tab]"));
    var cards = Array.prototype.slice.call(grid.querySelectorAll("[data-journal-card]"));
    var emptyMsg = document.querySelector("[data-journal-empty]");
    var animating = false;
    var LEAVE_MS = reduceMotion ? 0 : 260;
    var STAGGER_MS = reduceMotion ? 0 : 45;

    function cardMatches(card, tag) {
      if (!tag) return true; // "All"
      var cardTags = (card.dataset.tags || "").split(/\s+/);
      return cardTags.indexOf(tag) !== -1;
    }

    function getQueryParam(name) {
      var match = new RegExp("[?&]" + name + "=([^&]*)").exec(window.location.search);
      return match ? decodeURIComponent(match[1].replace(/\+/g, " ")) : "";
    }

    function setActiveTab(tag) {
      tabs.forEach(function (tab) {
        var isActive = tab.dataset.journalTab === tag;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });
    }

    function filterTo(tag) {
      if (animating) return;
      animating = true;
      grid.setAttribute("data-filtering", "true");

      var toShow = [];
      var toHide = [];
      cards.forEach(function (card) {
        var shouldShow = cardMatches(card, tag);
        var isHidden = card.classList.contains("is-hidden");
        if (shouldShow && isHidden) toShow.push(card);
        if (!shouldShow && !isHidden) toHide.push(card);
      });

      // Step 1: fade/rise out cards leaving the filtered set.
      toHide.forEach(function (card) { card.classList.add("is-leaving"); });

      window.setTimeout(function () {
        toHide.forEach(function (card) {
          card.classList.add("is-hidden");
          card.classList.remove("is-leaving");
        });

        // Step 2: bring in newly-matching cards with a staggered rise/fade.
        toShow.forEach(function (card, i) {
          card.classList.remove("is-hidden");
          card.classList.add("is-entering");
          card.style.transitionDelay = (i * STAGGER_MS) + "ms";
          // Force layout so the browser registers the "entering" (offset)
          // state before removing it — otherwise the transition is skipped.
          void card.offsetWidth;
          card.classList.remove("is-entering");
          card.classList.add("is-entered");
        });

        window.setTimeout(function () {
          toShow.forEach(function (card) {
            card.classList.remove("is-entered");
            card.style.transitionDelay = "";
          });
          grid.removeAttribute("data-filtering");
          animating = false;
        }, toShow.length ? STAGGER_MS * toShow.length + 450 : 0);

        var visibleCount = cards.filter(function (card) { return !card.classList.contains("is-hidden"); }).length;
        if (emptyMsg) emptyMsg.hidden = visibleCount !== 0;
      }, LEAVE_MS);
    }

    tabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        if (tab.classList.contains("is-active")) return;
        var tag = tab.dataset.journalTab || "";
        setActiveTab(tag);
        filterTo(tag);
      });
    });

    // Landing here from an article's tag link (?tag=the-land#journal-list):
    // apply the filter immediately, with no leave/enter animation, so the
    // page never flashes the full "All" set before settling on the tag.
    var incomingTag = getQueryParam("tag");
    if (incomingTag) {
      var matchedTab = tabs.some(function (tab) { return tab.dataset.journalTab === incomingTag; });
      if (matchedTab) {
        setActiveTab(incomingTag);
        var visibleCount = 0;
        cards.forEach(function (card) {
          var show = cardMatches(card, incomingTag);
          card.classList.toggle("is-hidden", !show);
          if (show) visibleCount++;
        });
        if (emptyMsg) emptyMsg.hidden = visibleCount !== 0;

        // Scroll to the section ourselves instead of using a #hash in the
        // URL. A native URL-fragment jump fires before smoothScrollModule
        // (above) has expanded the document's real scrollable height, gets
        // reset when that module switches #main to its fixed translated
        // layer, and the header's "scrolled" state — which only updates on
        // a real `scroll` event — falls out of sync with the page's actual
        // visual position when the browser silently re-jumps afterward.
        // Doing this explicitly, after that module's synchronous setup has
        // already run earlier in this same script, sidesteps the race.
        var journalSection = grid.closest("section") || document.getElementById("journal-list");
        if (journalSection) {
          var runScroll = function () { smoothScrollTo(journalSection); };
          if (document.readyState === "complete") {
            window.setTimeout(runScroll, 60);
          } else {
            window.addEventListener("load", function () { window.setTimeout(runScroll, 60); });
          }
        }
      }
    }
  })();

  /* =========================================================
     PRODUCT DETAILS PAGE — quantity stepper, wishlist toggles,
     add-to-cart / buy-now feedback. Guards itself out on any
     page that doesn't have this markup (e.g. the homepage).
     ========================================================= */
	 
  (function productDetailsModule() {
    var qtyWrap = document.querySelector(".pd-qty:not(.cart-qty)");
    if (qtyWrap) {
      var qtyInput = qtyWrap.querySelector("[data-qty-value]");
      var decreaseBtn = qtyWrap.querySelector("[data-qty-decrease]");
      var increaseBtn = qtyWrap.querySelector("[data-qty-increase]");
      var min = parseInt(qtyInput.min, 10) || 1;
      var max = parseInt(qtyInput.max, 10) || 99;

      function clampQty() {
        var val = parseInt(qtyInput.value, 10);
        if (isNaN(val)) val = min;
        val = Math.max(min, Math.min(max, val));
        qtyInput.value = val;
        return val;
      }
      if (decreaseBtn) decreaseBtn.addEventListener("click", function () {
        qtyInput.value = clampQty() - 1;
        clampQty();
      });
      if (increaseBtn) increaseBtn.addEventListener("click", function () {
        qtyInput.value = clampQty() + 1;
        clampQty();
      });
      qtyInput.addEventListener("change", clampQty);
    }

    // Wishlist toggle buttons — used by the product-page "Add to Wishlist"
    // link (.pd-wishlist) and the small heart buttons on the "You May Also
    // Love" cards (.wine-card-wishlist / .shop-wishlist). Delegated on the
    // document so it works no matter which of those markup patterns is present.
    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".pd-wishlist, .wine-card-wishlist, .shop-wishlist");
      if (!btn) return;
      btn.classList.toggle("is-active");
      var active = btn.classList.contains("is-active");
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      // The full-text wishlist link on the product page also swaps its label.
      var textNode = Array.prototype.slice.call(btn.childNodes).filter(function (n) {
        return n.nodeType === 3 && n.textContent.trim().length;
      })[0];
      if (textNode) {
        textNode.textContent = active ? " Added to Wishlist" : " Add to Wishlist";
      }
    });

    // "Add to Cart" primary button on the product page (kept separate from
    // the shared .btn-cart handler above so its icon markup is never
    // clobbered by a textContent swap).
    var pdCartBtn = document.querySelector("[data-pd-cart]");
    if (pdCartBtn) {
      pdCartBtn.addEventListener("click", function () {
        var variantId = pdCartBtn.getAttribute("data-variant-id");
        var qtyInput = document.querySelector(".pd-qty [data-qty-value]");
        var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        if (!variantId) return;
        var original = pdCartBtn.innerHTML;
        pdCartBtn.disabled = true;
        window.TenutaCart.add(variantId, quantity)
          .then(function () {
            pdCartBtn.innerHTML = "Added to Cart \u2713";
            if (window.__hasGSAP) gsap.fromTo(pdCartBtn, { scale: .96 }, { scale: 1, duration: .4, ease: "back.out(3)" });
            setTimeout(function () {
              pdCartBtn.innerHTML = original;
              pdCartBtn.disabled = false;
            }, 1800);
          })
          .catch(function () {
            pdCartBtn.innerHTML = "Couldn\u2019t add \u2014 retry";
            setTimeout(function () {
              pdCartBtn.innerHTML = original;
              pdCartBtn.disabled = false;
            }, 1800);
          });
      });
    }

    // "Buy Now" — adds the current variant/quantity to the cart, then
    // sends the shopper straight to Shopify checkout.
    var pdBuyNowBtn = document.querySelector("[data-pd-buy-now]");
    if (pdBuyNowBtn) {
      pdBuyNowBtn.addEventListener("click", function () {
        var variantId = pdBuyNowBtn.getAttribute("data-variant-id");
        var qtyInput = document.querySelector(".pd-qty [data-qty-value]");
        var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        if (!variantId) return;
        var original = pdBuyNowBtn.textContent;
        pdBuyNowBtn.textContent = "Redirecting\u2026";
        pdBuyNowBtn.disabled = true;
        window.TenutaCart.add(variantId, quantity)
          .then(function () { window.location.href = "/checkout"; })
          .catch(function () {
            pdBuyNowBtn.textContent = original;
            pdBuyNowBtn.disabled = false;
          });
      });
    }
  })();
  
  /* =========================================================
     SHOP PAGE — WORLDWIDE SHIPPING DROPDOWN
     Toggles the "+" beside "Worldwide Shipping" open to reveal a
     small list of regions and estimated delivery windows. Guards
     itself out on any page without the markup.
     ========================================================= */
  (function shippingPerkModule() {
    var btn = document.querySelector("[data-shipping-toggle]");
    var perk = document.querySelector("[data-shipping-perk]");
    if (!btn || !perk) return;

    btn.addEventListener("click", function () {
      var isOpen = perk.classList.toggle("is-open");
      btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  })();

  /* =========================================================
     ABOUT PAGE — READ MORE / READ LESS
     Generic, reusable accordion-style toggle. Guards itself
     out on any page without [data-readmore] markup. Height
     animation is handled entirely in CSS via the grid-rows
     trick (see .readmore-panel), so this only needs to flip
     a class and swap the button label/aria state.
     ========================================================= */
  (function readMoreModule() {
    var blocks = document.querySelectorAll("[data-readmore]");
    if (!blocks.length) return;

    blocks.forEach(function (block) {
      var btn = block.querySelector("[data-readmore-btn]");
      if (!btn) return;

      btn.addEventListener("click", function () {
        var isOpen = block.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
        btn.textContent = isOpen ? "Read Less" : "Read More";
      });
    });
  })();

  /* =========================================================
     ABOUT PAGE — OUR JOURNEY TIMELINE
     Draws the connecting line down the middle of the timeline in
     step with scroll progress through the track. Deliberately
     vanilla (no GSAP/ScrollTrigger dependency) so it can't go
     silently static if a CDN script fails to load or the smooth-
     scroll wrapper's transform doesn't line up with a scroll-
     trigger calibration — it just reads real, current element
     positions every frame. Guards itself out on pages without
     the markup.
     ========================================================= */
  (function journeyTimelineModule() {
    var track = document.querySelector("[data-journey-track]");
    var fill = document.querySelector("[data-journey-fill]");
    if (!track || !fill) return;

    if (reduceMotion) {
      fill.style.height = "100%";
      return;
    }

    var ticking = false;

    function progress() {
      var rect = track.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 when the track's top has just entered the bottom of the
      // viewport, 1 once the track's bottom has scrolled past the top.
      var total = rect.height + vh;
      if (total <= 0) return 0;
      var raw = (vh - rect.top) / total;
      return raw < 0 ? 0 : raw > 1 ? 1 : raw;
    }

    function update() {
      ticking = false;
      fill.style.height = (progress() * 110) + "%";
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    // The luxury smooth-scroll module lerps content toward the real
    // scroll position on its own rAF loop, which doesn't reliably fire
    // native "scroll" events every frame in every browser — so when
    // it's active, drive the line from a matching continuous loop to
    // keep it perfectly in sync with the visible (lerped) position.
    if (document.body.classList.contains("has-smooth-scroll")) {
      (function loop() {
        update();
        requestAnimationFrame(loop);
      })();
    }
  })();

  /* =========================================================
     ABOUT PAGE — FIND US MAP
     Reveals the pin drop + road path once the map scrolls into
     view (adds .is-active, which CSS uses to animate opacity/
     transform — see .map-wrap.is-active rules). Guards itself
     out on pages without the map markup.
     ========================================================= */
  (function findUsMapModule() {
    var mapWrap = document.querySelector("[data-map]");
    if (!mapWrap) return;

    function activate() {
      mapWrap.classList.add("is-active");
    }

    if ("IntersectionObserver" in window) {
      var mapObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            activate();
            mapObserver.unobserve(entry.target);
          }
        });
      }, { threshold: .3 });
      mapObserver.observe(mapWrap);
    } else {
      activate();
    }
 })();

  /* =========================================================
     CART PAGE — per-row quantity steppers, live totals,
     remove / clear cart. Guards itself out on any page
     without cart markup. Deliberately independent from the
     single-instance productDetailsModule() above (which only
     ever wires up the *first* .pd-qty on a page) so every row
     in the cart list gets its own working +/- controls.
     ========================================================= */
  (function cartPageModule() {
    var cartRoot = document.querySelector("[data-cart-root]");
    if (!cartRoot) return;

    // Talks to Shopify's real Ajax Cart API (/cart/change.js, /cart/clear.js)
    // using the Section Rendering API (?sections=...), so every response comes
    // back with freshly Liquid-rendered HTML for this section — correct prices,
    // minimum-bottle notice, shipping options, and checkout state included —
    // with NO page reload. The header cart-count bubble is patched separately
    // from the same response's item_count.
    var sectionId = cartRoot.getAttribute("data-section-id");
    var sectionRoot = document.querySelector("[data-cart-section-root]");
    if (!sectionId || !sectionRoot) return;

    var orderDetailsTimers = {};

    function setSectionBusy(busy) {
      sectionRoot.style.opacity = busy ? "0.55" : "";
      sectionRoot.style.pointerEvents = busy ? "none" : "";
    }

    function updateHeaderCartCount(count) {
      var bubble = document.querySelector("[data-cart-count]");
      if (!bubble) return;
      bubble.textContent = count;
      bubble.classList.toggle("is-empty", count === 0);
    }

    function replaceSection(html) {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var newRoot = parsed.querySelector("[data-cart-section-root]");
      if (!newRoot) return;
      sectionRoot.replaceWith(newRoot);
      sectionRoot = newRoot;

      // The freshly-parsed nodes are new to the DOM, so the page's one-time
      // scroll-reveal IntersectionObserver never sees them and they'd be
      // stuck in their pre-animation (blurred/hidden) state. They're already
      // on-screen post-swap, so just mark them visible immediately.
      if (sectionRoot.classList.contains("reveal")) sectionRoot.classList.add("is-visible");
      sectionRoot.querySelectorAll(".reveal").forEach(function (el) {
        el.classList.add("is-visible");
      });

      bindEvents();
    }

    function postCart(url, body) {
      setSectionBusy(true);
      return fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(Object.assign({ sections: sectionId }, body))
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && typeof data.item_count === "number") updateHeaderCartCount(data.item_count);
          if (data && data.sections && data.sections[sectionId]) replaceSection(data.sections[sectionId]);
          return data;
        })
        .catch(function (err) {
          console.error("Cart update failed:", err);
        })
        .finally(function () {
          setSectionBusy(false);
        });
    }

    function bindEvents() {
      var itemsList = sectionRoot.querySelector("[data-cart-items]");
      var clearBtn = sectionRoot.querySelector("[data-cart-clear]");
      var checkoutBtn = sectionRoot.querySelector("[data-checkout-btn]");

      if (itemsList) {
        itemsList.addEventListener("click", function (e) {
          var decBtn = e.target.closest("[data-qty-decrease]");
          var incBtn = e.target.closest("[data-qty-increase]");
          var removeBtn = e.target.closest("[data-cart-remove]");
          var row = (decBtn || incBtn || removeBtn) && (decBtn || incBtn || removeBtn).closest("[data-cart-item]");
          if (!row) return;
          var key = row.getAttribute("data-cart-key");
          if (!key) return;

          if (decBtn || incBtn) {
            var input = row.querySelector("[data-qty-value]");
            if (!input) return;
            var min = parseInt(input.min, 10) || 0;
            var max = parseInt(input.max, 10) || 99;
            var val = parseInt(input.value, 10);
            if (isNaN(val)) val = min;
            var nextVal = Math.max(min, Math.min(max, val + (decBtn ? -1 : 1)));
            if (nextVal === val) return; // already at the limit, nothing to send
            postCart("/cart/change.js", { id: key, quantity: nextVal });
          }

          if (removeBtn) {
            postCart("/cart/change.js", { id: key, quantity: 0 });
          }
        });

        itemsList.addEventListener("change", function (e) {
          var input = e.target.closest("[data-qty-value]");
          if (!input) return;
          var row = input.closest("[data-cart-item]");
          var key = row ? row.getAttribute("data-cart-key") : null;
          if (!key) return;
          var val = Math.max(0, parseInt(input.value, 10) || 0);
          postCart("/cart/change.js", { id: key, quantity: val });
        });
      }

      if (clearBtn) {
        clearBtn.addEventListener("click", function () {
          postCart("/cart/clear.js", {});
        });
      }

      if (checkoutBtn) {
        checkoutBtn.addEventListener("click", function (e) {
          if (checkoutBtn.classList.contains("is-disabled")) {
            e.preventDefault();
          }
        });
      }

      bindShippingSpeed();
      bindOrderDetails();
    }

    // EVENT NAME / SALES PERSON (cart attributes) — saves to Shopify's
    // cart-level `attributes`, which travel with the cart through checkout
    // and land on the order under "Additional details" in admin — no app
    // or line-item property needed. Debounced on typing, flushed immediately
    // on blur. Re-bound on every section swap (see bindEvents) since AJAX
    // cart updates (quantity change, remove, clear) replace this whole
    // section's DOM, including these fields — without re-binding here,
    // the inputs would go dead after the first cart change.
    function bindOrderDetails() {
      var fields = sectionRoot.querySelectorAll("[data-cart-attr]");
      if (!fields.length) return;
      var statusEl = sectionRoot.querySelector("[data-order-details-status]");
      var SAVE_DELAY = 700;

      function setStatus(text) {
        if (!statusEl) return;
        statusEl.textContent = text;
        if (text) {
          clearTimeout(setStatus._t);
          setStatus._t = setTimeout(function () { statusEl.textContent = ""; }, 2000);
        }
      }

      function save(key, value) {
        var attrs = {};
        attrs[key] = value;
        setStatus("Saving…");
        fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: attrs })
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Cart update failed: " + res.status);
            return res.json();
          })
          .then(function () { setStatus("Saved"); })
          .catch(function (err) {
            setStatus("Couldn't save — try again");
            console.error("Error saving cart attribute:", key, err);
          });
      }

      fields.forEach(function (field) {
        var key = field.getAttribute("data-cart-attr");
        field.addEventListener("input", function () {
          clearTimeout(orderDetailsTimers[key]);
          orderDetailsTimers[key] = setTimeout(function () { save(key, field.value); }, SAVE_DELAY);
        });
        field.addEventListener("blur", function () {
          clearTimeout(orderDetailsTimers[key]);
          save(key, field.value);
        });
      });
    }

    // Shipping cost isn't part of cart.total_price (Shopify only computes
    // real shipping at checkout), so this theme fakes a live total by
    // adding the selected radio's data-shipping-price on top of the
    // current subtotal, purely for display. Re-bound on every section
    // swap (see bindEvents) since AJAX cart updates replace these nodes;
    // subtotal is re-read fresh each time rather than cached, so it stays
    // correct after quantity changes.
    function bindShippingSpeed() {
      var radios = sectionRoot.querySelectorAll("[data-shipping-radio]");
      var shippingLine = sectionRoot.querySelector("[data-order-shipping]");
      var totalEl = sectionRoot.querySelector("[data-order-total]");
      if (!radios.length || !totalEl) return;

      var labels = {
        standard: "Standard (3 weeks) — 80€",
        express: "Express (5 days) — 160€"
      };

      function formatMoney(amount) {
        return amount.toFixed(0) + "€";
      }

      function reflectSelection(speed, price) {
        var subtotal = parseFloat(totalEl.getAttribute("data-subtotal")) || 0;
        if (shippingLine && labels[speed]) shippingLine.textContent = labels[speed];
        totalEl.textContent = formatMoney(subtotal + price);
      }

      var checked = sectionRoot.querySelector("[data-shipping-radio]:checked");
      if (checked) {
        reflectSelection(checked.value, parseFloat(checked.getAttribute("data-shipping-price")) || 0);
      }

      radios.forEach(function (radio) {
        radio.addEventListener("change", function (e) {
          var speed = e.target.value;
          var price = parseFloat(e.target.getAttribute("data-shipping-price")) || 0;
          reflectSelection(speed, price);

          fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: { shipping_speed: speed } })
          }).catch(function (err) {
            console.error("Error updating shipping speed:", err);
          });
        });
      });
    }

    bindEvents();
  })();

/* =========================================================
   CART PAGE — SHIPPING SPEED SELECTOR
   ========================================================= */
/* Shipping speed selection now lives inside cartPageModule (bindShippingSpeed),
   so it gets re-bound automatically every time an AJAX cart update swaps in
   fresh section HTML. See CART PAGE module above. */

  /* =========================================================
     PASSWORD VISIBILITY TOGGLE
     Generic — works for any [data-toggle-password] button that
     points at an input id via its data attribute (login and
     register panels both use this).
     ========================================================= */
  (function passwordToggleModule() {
    var toggles = document.querySelectorAll("[data-toggle-password]");
    if (!toggles.length) return;

    toggles.forEach(function (btn) {
      var input = document.getElementById(btn.getAttribute("data-toggle-password"));
      if (!input) return;
      btn.addEventListener("click", function () {
        var showing = input.type === "text";
        input.type = showing ? "password" : "text";
        btn.classList.toggle("is-active", !showing);
        btn.setAttribute("aria-pressed", showing ? "false" : "true");
        btn.setAttribute("aria-label", showing ? "Show password" : "Hide password");
      });
    });
  })();

  /* =========================================================
     LOGIN / REGISTER — tab switcher
     Toggles the .auth-tab / .auth-panel-content pairs, plus the
     "Create New Account" / "Already Have an Account" buttons at
     the bottom of each panel which jump to the other tab.
     ========================================================= */
  (function authTabsModule() {
    var tabs = document.querySelectorAll("[data-auth-tab]");
    if (!tabs.length) return;

    var panels = document.querySelectorAll("[data-auth-panel]");

    function activate(name) {
      tabs.forEach(function (tab) {
        var isActive = tab.getAttribute("data-auth-tab") === name;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });
      panels.forEach(function (panel) {
        var isActive = panel.getAttribute("data-auth-panel") === name;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        activate(tab.getAttribute("data-auth-tab"));
      });
    });

    document.querySelectorAll("[data-auth-switch]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        activate(btn.getAttribute("data-auth-switch"));
      });
    });
  })();

  /* =========================================================
     CONTACT FORM — AJAX submit via Section Rendering API
     Shopify's native {% form 'contact' %} is a real form that
     posts and re-renders the whole page on submit. This
     intercepts that, posts via fetch instead, and swaps in just
     the freshly-rendered contact section (success/error message
     included) — same pattern as the cart page's AJAX updates,
     and for the same reason: no full reload, no reveal-animation
     glitching from mid-flight page state.
     ========================================================= */
  function bindContactForm() {
    var form = document.querySelector("form[data-contact-form]");
    if (!form || form.dataset.ajaxBound === "true") return;
    form.dataset.ajaxBound = "true";

    var submitBtn = form.querySelector("[data-form-submit]");
    var thankYouModal = form.querySelector("[data-contact-thankyou]");
    var thankYouHeading = form.querySelector("[data-contact-thankyou-heading]");
    var closeEls = form.querySelectorAll("[data-contact-thankyou-close]");
    var nameInput = form.querySelector("#ContactFormName");
    var returnToInput = form.querySelector("[data-contact-return-to]");
    var basePath = window.location.origin + window.location.pathname;

    // IMPORTANT: relocate the modal to be a direct child of <body>, outside
    // #main. The smooth-scroll module keeps a permanent CSS `transform` on
    // #main, and per spec a transform on an ancestor turns any descendant
    // `position: fixed` element into something anchored to THAT ancestor's
    // box instead of the real viewport. Since #main wraps the entire page,
    // the modal's `inset: 0` was stretching to cover the full page height
    // (not just the visible screen) rather than centering on-screen — which,
    // combined with the (correct, intentional) scroll-lock below, made the
    // page look permanently frozen with no visible scrollbar. Moving the
    // modal out from under #main lets `position: fixed` behave normally.
    if (thankYouModal && thankYouModal.parentNode !== document.body) {
      document.body.appendChild(thankYouModal);
    }

    function openModal(name) {
      if (thankYouHeading) {
        thankYouHeading.textContent = name ? "Thank You, " + name + "!" : "Thank You!";
      }
      if (thankYouModal) {
        thankYouModal.hidden = false;
        thankYouModal.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
      }
    }

    function closeModal() {
      if (!thankYouModal) return;
      thankYouModal.hidden = true;
      thankYouModal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (window.location.href !== basePath) window.location.href = basePath;
    }

    closeEls.forEach(function (el) {
      el.addEventListener("click", closeModal);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && thankYouModal && !thankYouModal.hidden) closeModal();
    });

    // IMPORTANT: no preventDefault(), no fetch(). The form submits for
    // real — the exact same mechanism as your original working setup —
    // because Shopify's own spam-protection only accepts and delivers
    // the notification email for a genuine top-level page navigation.
    // We just tag where it comes back to with our own success/name
    // markers, so the reloaded page can auto-open the modal below.
    form.addEventListener("submit", function (e) {
      if (typeof form.reportValidity === "function" && !form.reportValidity()) {
        e.preventDefault();
        return;
      }
      if (submitBtn) {
        submitBtn.innerHTML = "Please wait\u2026";
        submitBtn.disabled = true;
      }
      var firstName = nameInput && nameInput.value ? nameInput.value.trim().split(" ")[0] : "";
      if (returnToInput) {
        var sep = returnToInput.value.indexOf("?") > -1 ? "&" : "?";
        returnToInput.value = returnToInput.value + sep + "contact_ok=1&name=" + encodeURIComponent(firstName);
      }
    });

    // On load: if we're back from a successful submission (our own
    // contact_ok marker is in the URL), open the modal automatically and
    // then strip the marker from the address bar without navigating again.
    var params = new URLSearchParams(window.location.search);
    if (params.get("contact_ok") === "1") {
      openModal(params.get("name") || "");
      history.replaceState(null, "", basePath);
    }
  }
  bindContactForm();
  // Safety net: if the contact section loaded slightly after this script
  // ran (e.g. lazy-loaded sections, slow connections), the querySelector
  // above would have found nothing and silently no-op'd, leaving the form
  // to fall back to a real native submit. bindContactForm() is idempotent
  // (guarded by data-ajax-bound), so retrying on window.load is safe and
  // will never double-attach the listener.
  window.addEventListener("load", bindContactForm);

  (function formSubmitFeedbackModule() {
    // Login / register forms still use Shopify's real full-page submit —
    // only the contact form (above) has been converted to AJAX so far.
    document.querySelectorAll("form[data-real-submit]:not([data-contact-form])").forEach(function (form) {
      var submitBtn = form.querySelector("[data-form-submit]");
      if (!submitBtn) return;
      form.addEventListener("submit", function (e) {
        if (typeof form.reportValidity === "function" && !form.reportValidity()) {
          e.preventDefault();
          return;
        }
        submitBtn.dataset.originalLabel = submitBtn.innerHTML;
        submitBtn.innerHTML = "Please wait\u2026";
        submitBtn.disabled = true;
      });
    });
  })();
})();

/* =========================================================
Footer slideshow
 ========================================================= */
	 
function createSlideshow(selector, interval = 4000) {
    const slides = document.querySelectorAll(selector);

    if (slides.length === 0) return;

    let current = 0;

    slides[current].classList.add("active");

    setInterval(() => {
        slides[current].classList.remove("active");

        current = (current + 1) % slides.length;

        slides[current].classList.add("active");
    }, interval);
}

createSlideshow(".footer-map-slide", 4000);
/* =========================================================
   WINE RULES POPUP ("Important Rules for Buying Wine Online")
   ========================================================= */
(function wineRulesPopupModule() {
  var popups = document.querySelectorAll("[data-wine-rules-popup]");
  if (!popups.length) return;

  var forcePreview = new URLSearchParams(window.location.search).get("wine_rules_preview") === "1";

  popups.forEach(function (popup) {
    var storageKey = "wineRulesPopup:" + popup.getAttribute("data-popup-id");
    var frequency = popup.getAttribute("data-frequency");
    var dismissible = popup.getAttribute("data-dismissible") === "true";
    var delay = parseInt(popup.getAttribute("data-delay"), 10) || 0;

    function alreadySeen() {
      if (forcePreview) return false;
      try {
        if (frequency === "every_page_load") return false;
        if (frequency === "once_per_session") {
          return sessionStorage.getItem(storageKey) === "1";
        }
        // once_per_day (or any other value): compare stored timestamp
        var stored = localStorage.getItem(storageKey);
        if (!stored) return false;
        var last = parseInt(stored, 10);
        if (isNaN(last)) return false;
        return (Date.now() - last) < 24 * 60 * 60 * 1000;
      } catch (e) {
        return false;
      }
    }

    function markSeen() {
      try {
        if (frequency === "once_per_session") {
          sessionStorage.setItem(storageKey, "1");
        } else {
          localStorage.setItem(storageKey, String(Date.now()));
        }
      } catch (e) { /* storage unavailable — popup will just reappear */ }
    }

    function openPopup() {
      popup.hidden = false;
      // Force layout so the transition below actually animates in.
      void popup.offsetWidth;
      popup.classList.add("is-open");
      popup.setAttribute("aria-hidden", "false");
      document.body.classList.add("nav-open");
    }

    function closePopup(markAsSeen) {
      popup.classList.remove("is-open");
      popup.setAttribute("aria-hidden", "true");
      document.body.classList.remove("nav-open");
      if (markAsSeen) markSeen();
      window.setTimeout(function () { popup.hidden = true; }, 400);
    }

    var closeBtn = popup.querySelector("[data-wine-rules-close]");
    var overlay = popup.querySelector("[data-wine-rules-overlay]");
    var agreeBtn = popup.querySelector("[data-wine-rules-agree]");

    if (agreeBtn) agreeBtn.addEventListener("click", function () { closePopup(true); });
    if (dismissible) {
      if (closeBtn) closeBtn.addEventListener("click", function () { closePopup(true); });
      if (overlay) overlay.addEventListener("click", function () { closePopup(true); });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && popup.classList.contains("is-open")) closePopup(true);
      });
    }

    if (!alreadySeen()) {
      window.setTimeout(openPopup, delay);
    }
  });
})();
/* =========================================================
   JOIN OUR WINE FAMILY — newsletter popup (click-triggered)
   Opens on click of any [data-join-family-trigger] element
   instead of navigating straight through. Same open/close/
   focus-trap pattern as wineRulesPopupModule above.

   Spam protection (free, no app):
   - Honeypot: contact[website] is hidden off-screen via CSS.
     Real visitors never see or fill it; simple bots often do.
     If it's filled on submit, the submission is silently blocked.
   - Time-trap: form_rendered_at is stamped with Date.now() the
     moment the popup opens. If submit happens faster than a
     human could realistically fill the form (<1.2s), it's blocked.
   Shopify's own backend spam filtering on native forms still
   applies underneath this — this is an extra client-side layer.
   ========================================================= */
(function joinFamilyPopupModule() {
  var popup = document.querySelector("[data-join-family-popup]");
  var triggers = document.querySelectorAll("[data-join-family-trigger]");
  if (!popup || !triggers.length) return;

  var form = popup.querySelector(".join-family-form");
  var timestampField = popup.querySelector("[data-join-family-timestamp]");
  var honeypotField = popup.querySelector('input[name="contact[website]"]');
  var MIN_FILL_TIME_MS = 1200;

  function openPopup() {
    popup.hidden = false;
    void popup.offsetWidth;
    popup.classList.add("is-open");
    popup.setAttribute("aria-hidden", "false");
    document.body.classList.add("nav-open");
    if (timestampField) timestampField.value = String(Date.now());
  }

  function closePopup() {
    popup.classList.remove("is-open");
    popup.setAttribute("aria-hidden", "true");
    document.body.classList.remove("nav-open");
    window.setTimeout(function () { popup.hidden = true; }, 400);
  }

  triggers.forEach(function (trigger) {
    trigger.addEventListener("click", function (e) {
      e.preventDefault();
      openPopup();
    });
  });

  var closeBtn = popup.querySelector("[data-join-family-close]");
  var overlay = popup.querySelector("[data-join-family-overlay]");
  if (closeBtn) closeBtn.addEventListener("click", closePopup);
  if (overlay) overlay.addEventListener("click", closePopup);
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && popup.classList.contains("is-open")) closePopup();
  });

  if (form) {
    form.addEventListener("submit", function (e) {
      var honeypotFilled = honeypotField && honeypotField.value.trim() !== "";
      var renderedAt = timestampField ? parseInt(timestampField.value, 10) : 0;
      var tooFast = renderedAt && (Date.now() - renderedAt) < MIN_FILL_TIME_MS;

      if (honeypotFilled || tooFast) {
        // Silently block: no error shown, so bots get no signal on why
        // it failed. Don't reset the honeypot value — leave the trap set.
        e.preventDefault();
      }
    });
  }

  // Shopify's customer form does a normal full-page POST, so a successful
  // submit or a validation error reloads the page. If that reload left a
  // result message inside the popup, reopen it so the person actually sees it.
  if (popup.querySelector(".join-family-form-message")) {
    openPopup();
  }
})();